import * as React from 'react';
import { router } from '@inertiajs/react';
import type { BarrioListItem } from '../../types/barrios-landing';

interface BarrioSearchProps {
  barrios: BarrioListItem[];
}

export function BarrioSearch({ barrios }: BarrioSearchProps) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const ref = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return barrios;
    const q = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return barrios.filter(b => {
      const name = b.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const city = b.city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return name.includes(q) || city.includes(q);
    });
  }, [query, barrios]);

  // Reset active index when filtered list changes
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [filtered]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !dropdownRef.current) return;
    const item = dropdownRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const navigate = (slug: string) => {
    setOpen(false);
    router.visit(`/barrios-privados/${slug}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      navigate(filtered[activeIndex].slug);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <section className="bp-search">
      <style>{`
        .bp-search {
          padding: 56px 24px;
          background: #fff;
        }
        .bp-search-inner {
          max-width: 560px;
          margin: 0 auto;
          text-align: center;
        }
        .bp-search h2 {
          font-size: 26px;
          font-weight: 800;
          color: #1a1a1a;
          margin: 0 0 6px;
        }
        .bp-search .section-sub {
          font-size: 16px;
          color: #666;
          margin: 0 0 24px;
        }
        .bp-search-box {
          position: relative;
        }
        .bp-search-input {
          width: 100%;
          padding: 14px 18px 14px 44px;
          font-size: 16px;
          border: 2px solid #ddd;
          border-radius: 12px;
          outline: none;
          transition: border-color 0.2s;
          background: #fafaf7;
          box-sizing: border-box;
        }
        .bp-search-input:focus {
          border-color: #1EAA25;
          background: #fff;
        }
        .bp-search-input::placeholder {
          color: #aaa;
        }
        .bp-search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 16px;
          pointer-events: none;
        }
        .bp-search-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          max-height: 320px;
          overflow-y: auto;
          z-index: 50;
        }
        .bp-search-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 18px;
          cursor: pointer;
          transition: background 0.15s;
          border-bottom: 1px solid #f0f0f0;
          text-decoration: none;
        }
        .bp-search-item:last-child {
          border-bottom: none;
        }
        .bp-search-item:hover,
        .bp-search-item.active {
          background: #f0fdf0;
        }
        .bp-search-item-name {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
        }
        .bp-search-item-city {
          font-size: 13px;
          color: #888;
        }
        .bp-search-item-arrow {
          color: #1EAA25;
          font-size: 14px;
          flex-shrink: 0;
        }
        .bp-search-empty {
          padding: 20px 18px;
          font-size: 15px;
          color: #888;
          text-align: center;
        }
        @media (max-width: 640px) {
          .bp-search h2 {
            font-size: 22px;
          }
        }
      `}</style>
      <div className="bp-search-inner">
        <h2>Buscá tu barrio</h2>
        <p className="section-sub">Encontrá cuándo entregamos en tu barrio privado</p>
        <div className="bp-search-box" ref={ref}>
          <i className="fa fa-search bp-search-icon" />
          <input
            className="bp-search-input"
            type="text"
            placeholder="Ej: Las Corzuelas, Manantiales..."
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />
          {open && (
            <div className="bp-search-dropdown" ref={dropdownRef}>
              {filtered.length > 0 ? (
                filtered.map((b, i) => (
                  <div key={b.slug} className={`bp-search-item${i === activeIndex ? ' active' : ''}`} onClick={() => navigate(b.slug)}>
                    <span className="bp-search-item-name">{b.name}, <span className="bp-search-item-city">{b.city}</span></span>
                    <i className="fa fa-chevron-right bp-search-item-arrow" />
                  </div>
                ))
              ) : (
                <div className="bp-search-empty">No encontramos ese barrio</div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
